import { useTranslation } from 'react-i18next';
import camelCase from 'lodash/camelCase';

import {
  Button,
  ButtonsWrapper,
  Form,
  Icon,
  ICONS,
  Loader,
  PLATFORM_THEME_COLORS,
  PopoverTooltip,
  SelectInput,
  SwitchInput,
  Typography,
} from '@thinknet-gmbh/platform-common-ui';

import {
  DatevSoftwareTargetOptionsEnum,
  PayrollAccountingTypeEnum,
} from '@thinknet-gmbh/platform-common';

import { useCreatePayrollAccountingSetting } from 'src/hooks/payroll-accounting/create-payroll-accounting-settings.hook';
import { useUpdatePayrollAccountingSetting } from 'src/hooks/payroll-accounting/update-payroll-accounting-settings.hook';
import { useGetPayrollAccountingSetting } from 'src/hooks/payroll-accounting/get-payroll-accounting-settings.hook';

import { StyledActiveButton, StyledLabelContainer } from './datev-setting.styles';

const DatevSetting = ({
  handleCreate,
  currentCompanyId,
}: {
  handleCreate: () => void;
  currentCompanyId?: string;
}) => {
  const { t } = useTranslation(['common', 'settings', 'employee']);

  const { setting, loading } = useGetPayrollAccountingSetting(
    currentCompanyId,
    PayrollAccountingTypeEnum.DATEV
  );

  const createPayrollAccountingSetting = useCreatePayrollAccountingSetting({});
  const updatePayrollAccountingSetting = useUpdatePayrollAccountingSetting({});

  const submitFn = setting?._id ? updatePayrollAccountingSetting : createPayrollAccountingSetting;

  const onSubmitHandler = (values: any) => {
    return submitFn({
      variables: {
        payload: {
          ...values,
          companyId: currentCompanyId,
          type: PayrollAccountingTypeEnum.DATEV,
        },
      },
    });
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <Form
      layout="vertical"
      onFinish={onSubmitHandler}
      initialValues={{
        softwareTarget: setting?.softwareTarget,
        active: setting?.active,
      }}
    >
      <StyledActiveButton>
        <StyledLabelContainer>
          <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
            {t('settings:datev.labels.active')}
          </Typography>
          <PopoverTooltip
            placement="top"
            overlayInnerStyle={{ width: '314px' }}
            trigger={'hover'}
            overlay={
              <Typography fontColor={PLATFORM_THEME_COLORS.primary.white}>
                {t('settings:datev.labels.activeInfo', { tabname: 'DATEV' })}
              </Typography>
            }
          >
            <div>
              <Icon
                icon={ICONS.infoCircleRegular}
                color={PLATFORM_THEME_COLORS.primary.blue}
                size={20}
              />
            </div>
          </PopoverTooltip>
        </StyledLabelContainer>

        <Form.Item name="active" valuePropName="checked">
          <SwitchInput defaultChecked={false} />
        </Form.Item>
      </StyledActiveButton>
      <Form.Item
        name={'softwareTarget'}
        label={t('settings:datev.labels.softwareTarget')}
        rules={[
          {
            required: true,
            message: t('common:validation.error.fieldRequired'),
          },
        ]}
      >
        <SelectInput placeholder={DatevSoftwareTargetOptionsEnum.LODAS}>
          {Object.keys(DatevSoftwareTargetOptionsEnum).map(target => (
            <SelectInput.Option value={target} key={target}>
              {t(`settings:datev.labels.softwareTargetOptions.${camelCase(target.toLowerCase())}`)}
            </SelectInput.Option>
          ))}
        </SelectInput>
      </Form.Item>

      <ButtonsWrapper marginTop={16}>
        <Button
          type="primary"
          icon={ICONS.plus}
          iconColor={PLATFORM_THEME_COLORS.primary.white}
          onClick={handleCreate}
        >
          {t('settings:datev.buttons.createConsultantNumber')}
        </Button>
      </ButtonsWrapper>

      <ButtonsWrapper marginTop={16}>
        <Button type="primary" loading={false} htmlType="submit">
          {t('common:buttons.save')}
        </Button>
      </ButtonsWrapper>
    </Form>
  );
};

export default DatevSetting;
